import ExcelJS from 'exceljs';

export type PoExcelPreview = {
  fileName: string;
  rows: string[][];
};

const MAX_ROWS = 50;
const MAX_EXTRACT_ROWS = 500;

/** Parse a single CSV line, respecting quoted fields (e.g. RE&S email tables). */
export function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
}

export async function readExcelRowsForExtract(file: File): Promise<string[][]> {
  const buffer = await file.arrayBuffer();
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';

  if (ext === 'csv') {
    const text = await file.text();
    return text
      .split(/\r?\n/)
      .filter((line) => line.trim())
      .slice(0, MAX_EXTRACT_ROWS)
      .map((line) => parseCsvLine(line));
  }

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const sheet = workbook.worksheets[0];
  if (!sheet) return [];

  const rows: string[][] = [];
  sheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (rowNumber > MAX_EXTRACT_ROWS) return;
    const values = (row.values as ExcelJS.CellValue[]) ?? [];
    // ExcelJS row.values is sparse — densify so callers never see undefined cells.
    const maxCol = Math.max(values.length - 1, row.cellCount, 0);
    const cells: string[] = [];
    for (let col = 1; col <= maxCol; col += 1) {
      cells.push(cellToString(values[col]));
    }
    rows.push(cells);
  });
  return rows;
}

function cellToString(value: ExcelJS.CellValue): string {
  if (value == null) return '';
  if (typeof value === 'object' && value !== null && 'text' in value) {
    return String((value as { text?: string }).text ?? '');
  }
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  return String(value);
}

export function isExcelFileName(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return ext === 'xlsx' || ext === 'xls' || ext === 'csv';
}

export function normalizePreviewRows(rows: string[][]): string[][] {
  if (rows.length === 0) return rows;

  const trimmed = rows.map((row) => row.map((cell) => String(cell ?? '').trim()));
  const maxCols = Math.max(...trimmed.map((row) => row.length), 0);
  const keepCols: number[] = [];

  for (let col = 0; col < maxCols; col += 1) {
    if (trimmed.some((row) => row[col])) keepCols.push(col);
  }
  if (keepCols.length === 0) return [];

  return trimmed
    .map((row) => keepCols.map((col) => row[col] ?? ''))
    .filter((row) => row.some(Boolean));
}

export async function readExcelPreview(file: File): Promise<PoExcelPreview> {
  const buffer = await file.arrayBuffer();
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';

  if (ext === 'csv') {
    const text = await file.text();
    const rows = text
      .split(/\r?\n/)
      .filter((line) => line.trim())
      .slice(0, MAX_ROWS)
      .map((line) => parseCsvLine(line));
    return { fileName: file.name, rows: normalizePreviewRows(rows) };
  }

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const sheet = workbook.worksheets[0];
  if (!sheet) {
    return { fileName: file.name, rows: [] };
  }

  const rows: string[][] = [];
  sheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (rowNumber > MAX_ROWS) return;
    const values = (row.values as ExcelJS.CellValue[]) ?? [];
    const maxCol = Math.max(values.length - 1, row.cellCount, 0);
    const cells: string[] = [];
    for (let col = 1; col <= maxCol; col += 1) {
      cells.push(cellToString(values[col]));
    }
    rows.push(cells);
  });

  return { fileName: file.name, rows: normalizePreviewRows(rows) };
}

export async function excelFileToExtractText(file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'csv') {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((line) => line.trim()).slice(0, MAX_ROWS);
    return lines.join('\n');
  }

  const preview = await readExcelPreview(file);
  if (preview.rows.length === 0) {
    throw new Error('Excel file appears to be empty.');
  }

  const lines = preview.rows.map((row) =>
    row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','),
  );
  return lines.join('\n');
}
